import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, Typography, useTheme } from "@mui/material";
import { CircleMinus, RefreshCw } from "lucide-react";
import {
	type FormEvent,
	type ReactNode,
	useEffect,
	useRef,
	useState,
} from "react";
import {
	type FieldValues,
	type UseFormReturn,
	useFieldArray,
	useForm,
} from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InstructionDrawer } from "web_myplann/Portfolio/Forms/ToolsShared/InstructionDrawer";
import { InstructionRow } from "web_myplann/Portfolio/Forms/ToolsShared/InstructionRow";
import { ToolValueDrawer } from "web_myplann/Portfolio/Forms/ToolsShared/ToolValueDrawer";
import { ToolValueFields } from "web_myplann/Portfolio/Forms/ToolsShared/ToolValueFields";
import type { ToolValueFormOutput } from "web_myplann/Portfolio/Forms/ToolsShared/toolValueFormSchema";
import type { InstructionTypeLabels } from "web_myplann/Portfolio/Forms/ToolsShared/useInstructionOptions";
import { applyToolValueUpdate } from "web_myplann/Portfolio/PortfolioShared/ManualValue/applyToolValueUpdate";
import { AddRowButton } from "web_myplann/Shared/Components/AddRowButton";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowHint } from "web_myplann/Shared/Components/FlatRow/FlatRowHint";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import type { z } from "zod";

type Props<
	TSchema extends z.ZodTypeAny,
	TInstructionSchema extends z.ZodTypeAny,
> = {
	open: boolean;
	onClose: () => void;
	schema: TSchema;
	defaultValues: z.input<TSchema>;
	onSave: (values: z.input<TSchema>) => void;
	onRemove?: () => void;
	isNew: boolean;
	instructions: ToolInstructionsConfig<TInstructionSchema>;
	getVariant?: (tool: FieldValues) => ToolVariant;
	renderIdentification?: (form: UseFormReturn<FieldValues>) => ReactNode;
};

type ToolInstructionsConfig<TInstructionSchema extends z.ZodTypeAny> = {
	name: "commodities" | "cryptocurrencyInstructions" | "instructions";
	schema: TInstructionSchema;
	defaults: z.input<TInstructionSchema>;
};

export type ToolVariant = {
	typeLabels?: InstructionTypeLabels;
	showInstructionCount?: boolean;
	showValues?: boolean;
	nameLabel?: string;
	instructionsHint?: string;
};

const DEFAULT_VARIANT: ToolVariant = {};

const PageResources = Resources.Portfolio.AddTools;

export const ToolDrawer = <
	TSchema extends z.ZodTypeAny,
	TInstructionSchema extends z.ZodTypeAny,
>({
	open,
	onClose,
	schema,
	defaultValues,
	onSave,
	onRemove,
	isNew,
	instructions,
	getVariant,
	renderIdentification,
}: Props<TSchema, TInstructionSchema>) => {
	const { t } = useResource();
	const theme = useTheme();
	const [editedInstruction, setEditedInstruction] = useState<number | null>(
		null,
	);
	const [isInstructionOpen, setIsInstructionOpen] = useState(false);
	const [isValueOpen, setIsValueOpen] = useState(false);

	const form = useForm({
		resolver: zodResolver(schema),
		defaultValues,
		mode: "onChange",
	});

	const instructionFields = useFieldArray({
		control: form.control,
		name: instructions.name,
	});

	const wasOpenRef = useRef(false);

	useEffect(() => {
		if (open && !wasOpenRef.current) {
			form.reset(defaultValues);
		}
		wasOpenRef.current = open;
	}, [open, defaultValues, form.reset]);

	const onSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.stopPropagation();
		void form.handleSubmit(() => {
			onSave(form.getValues());
		})(event);
	};

	const name = form.watch("name");
	const identification = form.watch("identification");
	const hasNameOrIdentification = Boolean(name?.trim() || identification);
	const isUnchangedEdit = !isNew && !form.formState.isDirty;
	const isSavedTool = form.watch("id") !== null;
	const variant = getVariant?.(form.watch()) ?? DEFAULT_VARIANT;

	const openInstruction = (index: number | null) => {
		setEditedInstruction(index);
		setIsInstructionOpen(true);
	};

	const addInstruction = () => {
		if (!hasNameOrIdentification) {
			form.setError("name", {
				type: "manual",
				message: t(PageResources.instructionsRequireTool),
			});
			return;
		}
		form.clearErrors("name");
		openInstruction(null);
	};

	const saveValue = (update: ToolValueFormOutput) => {
		const updated = applyToolValueUpdate(
			{
				value: form.getValues("value"),
				invested: form.getValues("invested"),
				valueDate: form.getValues("valueDate"),
				valueId: form.getValues("valueId"),
			},
			update,
		);
		for (const [field, value] of Object.entries(updated)) {
			form.setValue(field, value, { shouldDirty: true });
		}
	};

	const saveInstruction = (values: z.input<TInstructionSchema>) => {
		if (editedInstruction === null) {
			instructionFields.append(values);
			return;
		}

		instructionFields.update(editedInstruction, values);
	};

	const removeInstruction = () => {
		if (editedInstruction !== null) {
			instructionFields.remove(editedInstruction);
		}
		setIsInstructionOpen(false);
	};

	const canRemoveInstruction =
		editedInstruction !== null && !variant.instructionsHint;

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={t(PageResources.Drawers.tool)}
			grabber
		>
			<Stack component="form" onSubmit={onSubmit} pb={theme.spacingTokens.lg}>
				<Stack bgcolor={theme.palette.background.paper}>
					{renderIdentification?.(form)}
					<FlatRowFormInput
						control={form.control}
						name="name"
						label={variant.nameLabel ?? t(PageResources.Fields.name)}
						placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
						isLast={!variant.showValues}
					/>
					{variant.showValues && isSavedTool && (
						<AddRowButton
							label={t(PageResources.updateValue)}
							icon={RefreshCw}
							onClick={() => setIsValueOpen(true)}
						/>
					)}
					{variant.showValues && !isSavedTool && (
						<ToolValueFields control={form.control} isLast />
					)}
				</Stack>

				<Typography
					variant="overline"
					display="block"
					pt={2.4}
					px={theme.spacingTokens.lg}
					pb={theme.spacingTokens.sm}
				>
					{t(PageResources.Sections.instructions)}
				</Typography>

				<Stack bgcolor={theme.palette.background.paper}>
					{variant.instructionsHint && (
						<FlatRowHint text={variant.instructionsHint} />
					)}
					{instructionFields.fields.map((field, index) => (
						<InstructionRow
							key={field.id}
							investmentType={form.watch(
								`${instructions.name}.${index}.investmentType`,
							)}
							amount={form.watch(`${instructions.name}.${index}.amount`)}
							currency={form.watch(`${instructions.name}.${index}.currency`)}
							date={form.watch(`${instructions.name}.${index}.date`)}
							typeLabels={variant.typeLabels}
							isLast={index === instructionFields.fields.length - 1}
							onClick={() => openInstruction(index)}
						/>
					))}
					{!variant.instructionsHint && (
						<AddRowButton
							label={t(PageResources.Actions.addInstruction)}
							onClick={addInstruction}
						/>
					)}
				</Stack>

				<Stack px={theme.spacingTokens.lg} pt={theme.spacingTokens.lg}>
					<Button
						type="submit"
						variant="primary"
						disabled={!hasNameOrIdentification || isUnchangedEdit}
					>
						{t(
							isNew
								? PageResources.Actions.createTool
								: PageResources.Actions.saveChanges,
						)}
					</Button>
					{onRemove && (
						<Stack alignItems="center" pt={theme.spacingTokens.md}>
							<Button
								variant="textLink"
								color="error"
								startIcon={<CircleMinus size={theme.iconSize.sm} />}
								onClick={onRemove}
							>
								{t(PageResources.Actions.removeTool)}
							</Button>
						</Stack>
					)}
				</Stack>
			</Stack>

			<ToolValueDrawer
				open={isValueOpen}
				onClose={() => setIsValueOpen(false)}
				value={form.watch("value")}
				invested={form.watch("invested")}
				valueDate={form.watch("valueDate")}
				previousValues={form.watch("previousValues") ?? []}
				onSave={saveValue}
			/>

			<InstructionDrawer
				open={isInstructionOpen}
				onClose={() => setIsInstructionOpen(false)}
				schema={instructions.schema}
				defaultValues={
					editedInstruction === null
						? instructions.defaults
						: (form.getValues(
								`${instructions.name}.${editedInstruction}`,
							) as z.input<TInstructionSchema>)
				}
				onSave={saveInstruction}
				isNew={editedInstruction === null}
				typeLabels={variant.typeLabels}
				showCount={variant.showInstructionCount}
				onRemove={canRemoveInstruction ? removeInstruction : undefined}
			/>
		</MyPlannDrawer>
	);
};
